/**
 * Note: Before developing this content,
 * you must first define the theme token values the component.
 */
import { generateDynamicThemeToken } from '../../ThemeProvider';
import { getDynamicThemeToken, getSizeNumber, isCustomSize, isSize, transformSizeByNumber, getCustomStyleByNumberSize, } from './theme';
import { randomStr } from '../../Shared';
import { clipPathIdMap, iconStatusMap, sizeToClipPathMap, statusToShapMap, } from './constant';
const componentName = 'Avatar';
const classPrefix = `ui-Avatar`;
export const avatarClasses = {
    container: `${classPrefix}-container-${randomStr()}`,
    root: `${classPrefix}-root`,
    text: `${classPrefix}-text`,
    mask: `${classPrefix}-mask`,
    inactive: `${classPrefix}-inactive`,
    statusRoot: `${classPrefix}-status-root-${randomStr()}`,
    statusBadge: `${classPrefix}-status-badge`,
    status: (state) => {
        if (typeof state === 'string') {
            return `${classPrefix}-status-${state}`;
        }
        return `${classPrefix}-status`;
    },
    extrasSmall: `${classPrefix}-extrasSmall`,
    small: `${classPrefix}-small`,
    medium: `${classPrefix}-medium`,
    large: `${classPrefix}-large`,
    extraLarge: `${classPrefix}-extraLarge`,
    imageLoaded: `${classPrefix}-imageLoaded`,
};
const getNameBG = (initialColors, abbreviation) => {
    if (!abbreviation) {
        return initialColors[0];
    }
    let index = 0;
    const { length } = initialColors;
    for (let i = 0; i < abbreviation.length; i++) {
        index += abbreviation.charCodeAt(i);
        index %= length;
    }
    return initialColors[index];
};
export function getStyle(theme, opts) {
    const size = opts.size ?? 'medium';
    const { status, color, backgroundColor } = opts;
    const dynamicToken = getDynamicThemeToken(theme);
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const widthSize = isSize(size) ? tk(token.width[size]) : size;
    const heightSize = isSize(size) ? tk(token.height[size]) : size;
    const _size = isSize(size) ? size : transformSizeByNumber(size);
    // 'extraSmall' Avatars currently do not support status
    let clipPath = 'unset';
    if (!['extraSmall'].includes(_size) &&
        typeof status === 'string' &&
        iconStatusMap[status]) {
        if (isCustomSize(size)) {
            clipPath = `url('#${clipPathIdMap[statusToShapMap[status]]}')`;
        }
        else {
            clipPath = `path('${sizeToClipPathMap[_size][statusToShapMap[status]]}')`;
        }
    }
    const customSizeStyle = typeof size === 'number' ? getCustomStyleByNumberSize(size) : null;
    const style = {
        [`&.${avatarClasses.container}`]: {
            position: 'relative',
            display: 'inline-flex',
            height: heightSize,
            width: widthSize,
            borderRadius: tk(token.borderRadius[_size]),
            ...(typeof size === 'number' && customSizeStyle
                ? {
                    borderRadius: customSizeStyle.borderRadius,
                }
                : {}),
            background: tk(token.backgroundColor),
            overflow: 'visible',
            flexShrink: 0,
            '&:focus-visible': {
                outline: tk(token.outline.focusVisible),
                outlineOffset: 2,
            },
            [`& .${avatarClasses.root}`]: {
                color: typeof color === 'string' && color ? color : tk(token.color),
                backgroundColor: typeof backgroundColor === 'string' && backgroundColor
                    ? backgroundColor
                    : getNameBG(token.initialColors, opts.abbreviation),
                [`&.${avatarClasses.imageLoaded}`]: {
                    background: tk(token.backgroundColor),
                },
            },
            [`.${avatarClasses.mask}`]: {
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                opacity: 0,
                borderRadius: tk(token.borderRadius[_size]),
                ...(typeof size === 'number' && customSizeStyle
                    ? {
                        borderRadius: customSizeStyle.borderRadius,
                    }
                    : {}),
            },
            [`&.${avatarClasses.inactive}`]: {
                [`.${avatarClasses.mask}`]: {
                    opacity: 1,
                    background: tk(token.mask.backgroundColor.inactive),
                    clipPath,
                },
            },
            [`&.${avatarClasses.small}`]: {
                [`.${avatarClasses.root}`]: {
                    // lineHeight: 'unset',
                    lineHeight: tk(token.height[_size]),
                },
            },
            [`.${avatarClasses.root}`]: {
                height: '100%',
                width: '100%',
                fontFamily: tk(token.fontFamily),
                fontSize: tk(token.fontSize[_size]),
                fontWeight: tk(token.fontWeight[_size]),
                borderRadius: tk(token.borderRadius[_size]),
                ...(typeof size === 'number' && customSizeStyle
                    ? {
                        borderRadius: customSizeStyle.borderRadius,
                        fontSize: customSizeStyle.fontSize,
                    }
                    : {}),
                clipPath,
            },
        },
        [`button&.${avatarClasses.container}`]: {
            appearance: 'none',
            border: 'none',
            padding: 0,
            boxShadow: 'none',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: '#fff',
                [`.${avatarClasses.mask}`]: {
                    opacity: 1,
                    background: tk(token.mask.backgroundColor.hover),
                    clipPath,
                },
            },
            '&:active': {
                [`.${avatarClasses.mask}`]: {
                    opacity: 1,
                    background: tk(token.mask.backgroundColor.press),
                    clipPath,
                },
            },
        },
    };
    return style;
}
export function getStatusStyle(theme, opts) {
    const size = opts.size ?? 'medium';
    const _size = isSize(size) ? size : transformSizeByNumber(size);
    const dynamicToken = getDynamicThemeToken(theme);
    const [token] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const isInternalStatus = typeof opts.status === 'string' && iconStatusMap[opts.status];
    const scaleSize = isCustomSize(size)
        ? (size === 'extraLarge' ? 110 : size) / 32
        : token.status.scale[_size];
    let translate = opts.status === 'inMeeting'
        ? 'translate(32%, -25%)'
        : 'translate(25%, -25%)';
    if (isCustomSize(size)) {
        const sizeNumber = getSizeNumber(size);
        const _distance = (sizeNumber / 40) * 12;
        const width = scaleSize * (opts.status === 'inMeeting' ? 12 : 8);
        const wDistance = _distance - width;
        translate = `translate(${(Math.abs(wDistance) * 100) / width}%, -10%)`;
    }
    const defaultBadgeStyle = {
        minWidth: 'unset',
        padding: 0,
        transform: `scale(${scaleSize}) ${translate}`,
        zIndex: 0,
        height: '8px',
        svg: {
            width: '8px',
            height: '8px',
        },
        [`&.${avatarClasses.status('inMeeting')}`]: {
            svg: {
                height: '8px',
                width: '12px',
            },
        },
    };
    if (!isInternalStatus) {
        defaultBadgeStyle.transform = 'unset';
    }
    const style = {
        [`&.${avatarClasses.statusRoot}`]: {
            width: '100%',
            height: '100%',
            margin: 0,
            [`& .${avatarClasses.statusBadge}`]: {
                ...defaultBadgeStyle,
            },
        },
    };
    return style;
}
